import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FileUp, Upload, CalendarClock } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/ui/Button';
import nurseryService from '../../services/nursery';
import nurseryRetailService from '../../services/nurseryRetail';
import { useModuleLicense } from '../../hooks/useModuleLicense';
import { errorMessage } from '../../utils/apiResponse';
import { ManagerSeatRequired, SeatLoading } from '../../components/nursery/retail/ManagerSeatRequired';
import { PoImportCard } from '../../components/nursery/retail/PoImportCard';
import type { MatchOption } from '../../components/nursery/retail/PoLineMatcher';
import type { PoImportFile, PoImportLine, PoImportPreview, PoImportResult } from '../../types/nurseryRetail';

/**
 * /nursery/orders/import — a retailer's purchase order files become orders.
 *
 * Choose the files as they were downloaded (one per purchase order). Each is
 * read and judged by the server and shown here; nothing is written until
 * Import. A purchase order with something wrong is held, says why, and is not
 * imported until that is sorted; the others still go. Import sends the files
 * again and the server judges them afresh, so what is made is what it read.
 *
 * docs/retailer-po-and-cross-dock-plan.md
 */

const MAX_FILES = 60;

function PoImport() {
  const { t } = useTranslation('nursery', { keyPrefix: 'PoImport' });
  const navigate = useNavigate();
  const { hasAccess: canManage, loading: licenseLoading } = useModuleLicense('nursery', ['manager', 'admin']);

  const [files, setFiles] = useState<PoImportFile[]>([]);
  const [preview, setPreview] = useState<PoImportPreview | null>(null);
  const [done, setDone] = useState<PoImportResult[]>([]);
  const [busy, setBusy] = useState<'reading' | 'checking' | 'importing' | 'matching' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [options, setOptions] = useState<MatchOption[]>([]);
  const fileInput = useRef<HTMLInputElement>(null);

  /** Judge these files and show the result. The files and the results stay in step by position. */
  const check = useCallback(async (next: PoImportFile[]) => {
    setFiles(next);
    if (!next.length) {
      setPreview(null);
      return;
    }
    setBusy('checking');
    setError(null);
    try {
      const res = await nurseryRetailService.previewPurchaseOrders(next);
      if (res.success) setPreview(res.data);
      else setError(errorMessage(res, t('readFailed')));
    } catch (e) {
      setError(errorMessage(e, t('readFailed')));
    } finally {
      setBusy(null);
    }
  }, [t]);

  // What an unmatched line can be matched to. Only read once one needs it.
  const needsOptions = Boolean(preview?.files.some((f) => f.lines?.some((l) => l.issues.includes('unmatched'))));
  useEffect(() => {
    if (!needsOptions || options.length) return;
    let cancelled = false;
    (async () => {
      try {
        const [products, stockItems] = await Promise.all([
          nurseryService.getProducts({ active: true }),
          nurseryService.getStockItems({ active: true, kind: 'plant' }),
        ]);
        if (cancelled) return;
        const list: MatchOption[] = [];
        for (const p of products.success ? products.data || [] : []) {
          list.push({ kind: 'product', id: p.id, label: `${p.name}${p.tree_type_name ? ` · ${p.tree_type_name}` : ''}` });
        }
        for (const s of stockItems.success ? stockItems.data || [] : []) {
          const name = [s.cultivar_name, s.rootstock_name].filter(Boolean).join(' / ');
          list.push({ kind: 'stock_item', id: s.id, label: t('stockItemOption', { name: `${name}${s.tree_type_name ? ` · ${s.tree_type_name}` : ''}` }) });
        }
        setOptions(list);
      } catch {
        // The matcher then offers nothing, and says how many it has.
      }
    })();
    return () => { cancelled = true; };
  }, [needsOptions, options.length, t]);

  const addFiles = async (list: FileList | null) => {
    if (!list || !list.length) return;
    setBusy('reading');
    setError(null);
    try {
      // The same file chosen twice is one purchase order.
      const have = new Set(files.map((f) => f.text));
      const next = [...files];
      for (const f of Array.from(list)) {
        const text = await f.text();
        if (have.has(text)) continue;
        have.add(text);
        next.push({ name: f.name, text });
      }
      if (next.length > MAX_FILES) {
        setError(t('tooMany', { max: MAX_FILES }));
        setBusy(null);
        return;
      }
      await check(next);
    } catch (e) {
      setError(errorMessage(e, t('filesUnreadable')));
      setBusy(null);
    } finally {
      if (fileInput.current) fileInput.current.value = '';
    }
  };

  const change = (i: number, patch: Partial<PoImportFile>) =>
    check(files.map((f, idx) => (idx === i ? { ...f, ...patch } : f)));

  const remove = (i: number) => check(files.filter((_, idx) => idx !== i));

  const match = async (line: PoImportLine, option: MatchOption) => {
    setBusy('matching');
    setError(null);
    try {
      const res = await nurseryRetailService.rememberMatch({
        barcode: line.barcode,
        item_number: line.item_number,
        description: line.description,
        ...(option.kind === 'product' ? { product_id: option.id } : { stock_item_id: option.id }),
      });
      if (!res.success) {
        setError(errorMessage(res, t('matchFailed')));
        setBusy(null);
        return;
      }
      // Every purchase order naming that tree is matched now.
      await check(files);
    } catch (e) {
      setError(errorMessage(e, t('matchFailed')));
      setBusy(null);
    }
  };

  const ready = useMemo(
    () => (preview ? files.filter((_, i) => preview.files[i]?.ready) : []),
    [files, preview],
  );

  const importReady = async () => {
    if (!preview || !ready.length) return;
    setBusy('importing');
    setError(null);
    try {
      const res = await nurseryRetailService.confirmPurchaseOrders(ready);
      if (!res.success) {
        setError(errorMessage(res, t('importFailed')));
        setBusy(null);
        return;
      }
      const made = res.data.files.filter((f) => f.created);
      const madeHashes = new Set(made.map((f) => f.file_hash));
      setDone((d) => [...made, ...d]);
      // What was held, and anything the server refused on the way through,
      // stays on the page to be sorted.
      const held = files.filter((_, i) => !madeHashes.has(preview.files[i]?.file_hash ?? ''));
      await check(held);
    } catch (e) {
      setError(errorMessage(e, t('importFailed')));
      setBusy(null);
    }
  };

  if (licenseLoading) return <SeatLoading />;
  if (!canManage) return <ManagerSeatRequired what={t('seatWhat')} />;

  const heldCount = preview ? preview.files.filter((f) => !f.ready).length : 0;
  const working = busy !== null;

  return (
    <>
      <PageHeader
        title={t('title')}
        subtitle={t('subtitle')}
        icon={<FileUp className="h-6 w-6 text-white" />}
        actionButtons={
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" onClick={() => navigate('/nursery/cross-dock')}>
              <CalendarClock className="h-4 w-4" /> {t('crossDockSchedules')}
            </Button>
          </div>
        }
      />
      <div className="max-w-4xl mx-auto px-4 py-6 space-y-5">
        {error && <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700 break-words">{error}</div>}

        <section className="bg-white border border-neutral-200 rounded-xl p-4 space-y-3">
          <p className="text-sm text-neutral-600">
            {t('intro')}
          </p>
          <input
            ref={fileInput}
            id="po-files"
            type="file"
            accept=".csv,text/csv"
            multiple
            className="sr-only"
            onChange={(e) => addFiles(e.target.files)}
          />
          <div className="flex flex-wrap items-center gap-2">
            <Button size="sm" variant={files.length ? 'outline' : 'primary'} disabled={working} onClick={() => fileInput.current?.click()}>
              <Upload className="h-4 w-4" /> {files.length ? t('addMore') : t('choose')}
            </Button>
            {busy && (
              <span role="status" className="text-sm text-neutral-500">
                {busy === 'reading' ? t('busy.reading')
                  : busy === 'importing' ? t('busy.importing')
                    : busy === 'matching' ? t('busy.matching') : t('busy.checking')}
              </span>
            )}
          </div>
        </section>

        {preview && files.length > 0 && (
          <section aria-label={t('readLabel')} className="space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h2 className="text-sm font-semibold text-neutral-800">
                {t('readyCount', { count: ready.length })}
                {heldCount > 0 && <span className="text-red-700">{t('heldCount', { count: heldCount })}</span>}
              </h2>
              <Button size="sm" disabled={working || ready.length === 0} onClick={importReady}>
                {busy === 'importing'
                  ? t('busy.importing')
                  : t('importButton', { count: ready.length })}
              </Button>
            </div>
            {heldCount > 0 && (
              <p className="text-sm text-neutral-600">
                {t('heldHint')}
              </p>
            )}
            <ul className="space-y-3">
              {preview.files.map((result, i) => (
                <PoImportCard
                  key={`${result.file_hash}-${i}`}
                  result={result}
                  file={files[i]}
                  farms={preview.farms}
                  options={options}
                  busy={working}
                  onChange={(patch) => change(i, patch)}
                  onRemove={() => remove(i)}
                  onMatch={match}
                />
              ))}
            </ul>
          </section>
        )}

        {done.length > 0 && (
          <section aria-label={t('importedLabel')} className="space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h2 className="text-sm font-semibold text-neutral-800">
                {t('importedCount', { count: done.length })}
              </h2>
              <Button size="sm" variant="outline" onClick={() => navigate('/nursery/despatch')}>{t('openPlanner')}</Button>
            </div>
            <ul className="space-y-3">
              {done.map((result) => (
                <PoImportCard key={result.file_hash} result={result} farms={[]} options={[]} busy={false} />
              ))}
            </ul>
          </section>
        )}
      </div>
    </>
  );
}

export default PoImport;
